/**
 * Задачи (§51 «Jobs»).
 *
 * Единственный экран, где видно имя провайдера и себестоимость (§41, §35).
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import type { AdminJobRow } from '@alterium/shared';
import { adminApi } from '../../api/admin';
import { DataTable, type Column } from '../../components/admin/DataTable';
import { formatDateTime } from '../../i18n/format';
import { useTranslation } from 'react-i18next';

const statuses = ['', 'queued', 'processing', 'completed', 'failed', 'cancelled'];

const time = (value: string): string =>
  formatDateTime(value, {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });

export function AdminJobsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState('');
  const [query, setQuery] = useState('');

  const jobs = useQuery({
    queryKey: ['admin', 'jobs', page, status, query],
    queryFn: () => adminApi.jobs({ page, status, query }),
  });

  const columns: Array<Column<AdminJobRow>> = [
    { key: 'created', title: t('admin.job.created'), render: (row) => time(row.createdAt), width: '110px' },
    { key: 'operation', title: t('admin.job.operation'), render: (row) => row.operation },
    { key: 'user', title: t('admin.job.user'), render: (row) => row.userEmail },
    {
      key: 'status',
      title: t('admin.job.status'),
      render: (row) => (
        <span className={row.status === 'failed' ? 'text-error' : 'text-content'}>
          {row.status}
        </span>
      ),
    },
    {
      key: 'error',
      title: t('admin.job.error'),
      render: (row) => <span className="text-content-secondary">{row.errorCode ?? '—'}</span>,
    },
    { key: 'provider', title: t('admin.job.provider'), render: (row) => row.provider ?? '—' },
    { key: 'attempts', title: t('admin.job.attempts'), render: (row) => row.attempts, align: 'right' },
    { key: 'units', title: t('admin.job.spent'), render: (row) => row.unitsSpent, align: 'right' },
    {
      key: 'cost',
      title: t('admin.job.cost'),
      render: (row) => `$${(row.costCents / 100).toFixed(2)}`,
      align: 'right',
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap gap-2">
        <input
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            setPage(1);
          }}
          placeholder={t('admin.job.searchPlaceholder')}
          className="h-9 w-64 rounded-control border border-line bg-surface px-3 text-meta text-content placeholder:text-content-muted focus:border-accent focus:outline-none"
        />
        <select
          value={status}
          onChange={(event) => {
            setStatus(event.target.value);
            setPage(1);
          }}
          className="h-9 rounded-control border border-line bg-surface px-3 text-meta text-content focus:border-accent focus:outline-none"
        >
          {statuses.map((value) => (
            <option key={value} value={value}>
              {value || t('admin.allStatuses')}
            </option>
          ))}
        </select>
      </div>

      <DataTable
        columns={columns}
        rows={jobs.data?.items ?? []}
        rowKey={(row) => row.id}
        total={jobs.data?.total}
        page={page}
        totalPages={jobs.data?.totalPages}
        loading={jobs.isLoading}
        onPage={setPage}
        onRowClick={(row) => navigate(`/admin/jobs/${row.id}`)}
      />
    </div>
  );
}
